import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MOCK_MODE } from './api';

export type LiveStatus = 'off' | 'connecting' | 'open' | 'reconnecting';

/** PLAN §10.2 olay tipleri; bilinmeyen tipler yok sayılır. */
const EVENT_TYPES = [
  'message.new',
  'message.status',
  'conversation.updated',
  'wa.status',
  'ai.thinking',
  'ai.decision',
  'ticket.created',
  'test_mode.changed',
  'policy.block'
] as const;

interface SseEnvelope {
  id?: number;
  type?: string;
  ts?: string;
  data?: Record<string, unknown>;
}

/**
 * GET /api/events (EventSource, çerez oturumu) canlı akışı.
 * - Bağlantı koparsa üstel geri çekilmeyle (1s→30s) yeniden bağlanır.
 * - Gelen olay, ilgili react-query anahtarlarını tazeler; ekranlar ayrıca polling yapmaz.
 * - Tarayıcı Last-Event-ID başlığını kendisi gönderir; kaçan olaylar replaysiz kalmaz.
 */
export function useLiveEvents(enabled: boolean): LiveStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>(enabled ? 'connecting' : 'off');
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!enabled || MOCK_MODE || typeof EventSource === 'undefined') {
      setStatus('off');
      return;
    }

    let closed = false;
    let source: EventSource | null = null;
    let timer: number | undefined;
    let attempt = 0;

    const invalidateConversation = (data: Record<string, unknown>) => {
      const conversationId = typeof data.conversationId === 'string' ? data.conversationId : null;
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      if (conversationId) void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    };

    const handleEvent = (event: MessageEvent) => {
      let payload: SseEnvelope = {};
      try {
        payload = JSON.parse(event.data) as SseEnvelope;
      } catch {
        return;
      }
      const type = payload.type || event.type;
      const data = payload.data ?? {};

      switch (type) {
        case 'message.new':
        case 'message.status':
          invalidateConversation(data);
          break;
        case 'conversation.updated':
          invalidateConversation(data);
          break;
        case 'test_mode.changed':
          void queryClient.invalidateQueries({ queryKey: ['test-mode'] });
          break;
        case 'wa.status':
          void queryClient.invalidateQueries({ queryKey: ['wa-status'] });
          break;
        case 'ticket.created':
          void queryClient.invalidateQueries({ queryKey: ['tickets'] });
          break;
        case 'policy.block':
          invalidateConversation(data);
          break;
        default:
          break;
      }
    };

    const connect = () => {
      if (closed) return;
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
      source = new EventSource('/api/events', { withCredentials: true });

      source.onopen = () => {
        attempt = 0;
        setStatus('open');
      };

      for (const type of EVENT_TYPES) {
        source.addEventListener(type, handleEvent as EventListener);
      }

      source.onerror = () => {
        source?.close();
        source = null;
        if (closed) return;
        attempt += 1;
        setStatus('reconnecting');
        const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
        timer = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      if (timer) window.clearTimeout(timer);
      source?.close();
      setStatus('off');
    };
  }, [enabled, queryClient]);

  return status;
}
